import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Check, Copy, Clock, Search as SearchIcon, ShieldCheck, XCircle, AlertTriangle, RotateCcw, Loader2,
  UploadCloud, FileText, X, ArrowLeftRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  Pending: { cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
  "Under Review": { cls: "bg-sky-50 text-sky-800 border-sky-200", Icon: SearchIcon },
  Approved: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: ShieldCheck },
  Rejected: { cls: "bg-rose-50 text-rose-800 border-rose-200", Icon: XCircle },
  Failed: { cls: "bg-zinc-100 text-zinc-800 border-zinc-200", Icon: AlertTriangle },
  Refunded: { cls: "bg-violet-50 text-violet-800 border-violet-200", Icon: RotateCcw },
  Completed: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: Check },
  Processing: { cls: "bg-sky-50 text-sky-800 border-sky-200", Icon: Loader2 },
};

export const StatusBadge = ({ status, className }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Pending;
  const Icon = s.Icon;
  return (
    <span
      data-testid="status-badge"
      data-status={status}
      className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium", s.cls, className)}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
      {status}
    </span>
  );
};

export const CopyButton = ({ value, label = "Copy", testId = "copy-button", size = "sm", variant = "outline", className }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setDone(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setDone(false), 1600);
  };
  return (
    <Button type="button" size={size} variant={variant} onClick={copy} data-testid={testId} className={cn("btn-lift gap-1.5", className)} aria-label={label}>
      {done ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      {label && <span>{done ? "Copied" : label}</span>}
    </Button>
  );
};

/** Loads a private file (receipt) through the authenticated API client as a blob. */
export const SecureFile = ({ url, client, className, testId = "secure-file" }) => {
  const [state, setState] = useState({ loading: true, src: null, type: null, error: null });
  useEffect(() => {
    let revoked = null;
    let alive = true;
    if (!url) return;
    setState({ loading: true, src: null, type: null, error: null });
    client
      .get(url.replace(/^\/api/, ""), { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        revoked = URL.createObjectURL(r.data);
        setState({ loading: false, src: revoked, type: r.data.type, error: null });
      })
      .catch(() => alive && setState({ loading: false, src: null, type: null, error: "Could not load file" }));
    return () => {
      alive = false;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [url, client]);
  if (!url) return <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">No receipt uploaded</div>;
  if (state.loading) return <div className={cn("h-48 animate-pulse rounded-lg bg-muted", className)} />;
  if (state.error) return <div className="rounded-lg border p-4 text-sm text-rose-700">{state.error}</div>;
  if (state.type === "application/pdf")
    return (
      <Button variant="outline" data-testid={testId} onClick={() => window.open(state.src, "_blank")} className="gap-2">
        <FileText className="h-4 w-4" /> Open PDF receipt
      </Button>
    );
  if (state.type && state.type.startsWith("video/"))
    return (
      <video
        src={state.src}
        controls
        data-testid={testId}
        className={cn("max-h-96 w-full rounded-lg border bg-black", className)}
      />
    );
  return (
    <a href={state.src} target="_blank" rel="noreferrer" data-testid={testId}>
      <img src={state.src} alt="Payment receipt" className={cn("max-h-80 w-full rounded-lg border bg-white object-contain", className)} />
    </a>
  );
};

export const QrImage = ({ url, className, testId = "qr-image" }) => {
  const [err, setErr] = useState(false);
  if (!url || err)
    return (
      <div className={cn("flex aspect-square items-center justify-center rounded-xl border border-dashed bg-muted text-xs text-muted-foreground", className)}>
        {err ? "QR unavailable" : "No QR"}
      </div>
    );
  return <img src={fileUrl(url)} alt="Payment QR code" onError={() => setErr(true)} data-testid={testId} className={cn("aspect-square rounded-xl border bg-white object-contain p-2", className)} />;
};

export const FileUploader = ({ onUpload, accept = "image/png,image/jpeg,image/webp,application/pdf", value, onClear, testId = "receipt-uploader", label = "Upload payment receipt", hint = "PNG, JPG, WEBP or PDF · max 5 MB" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const pick = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5 MB)");
    setBusy(true);
    try {
      if (file.type.startsWith("image/")) setPreview(URL.createObjectURL(file));
      else setPreview(null);
      await onUpload(file);
    } catch {
      setPreview(null);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div data-testid={testId}>
      <input ref={ref} type="file" accept={accept} className="hidden" data-testid={`${testId}-input`} onChange={(e) => pick(e.target.files?.[0])} />
      {value ? (
        <div className="flex items-center gap-3 rounded-xl border bg-card p-3">
          {preview ? <img src={preview} alt="" className="h-14 w-14 rounded-lg border object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted"><FileText className="h-5 w-5 text-muted-foreground" /></div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium" data-testid={`${testId}-filename`}>{value.filename}</p>
            <p className="text-xs text-emerald-700">Uploaded securely</p>
          </div>
          <Button type="button" size="icon" variant="ghost" aria-label="Remove file" data-testid={`${testId}-remove`} onClick={() => { setPreview(null); onClear?.(); }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          data-testid={`${testId}-button`}
          className="flex w-full items-center gap-3 rounded-xl border border-dashed bg-card p-4 text-left transition-colors hover:border-primary hover:bg-secondary"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
            {busy ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <UploadCloud className="h-5 w-5 text-primary" />}
          </div>
          <div>
            <p className="text-sm font-medium">{busy ? "Uploading…" : label}</p>
            <p className="text-xs text-muted-foreground">{hint}</p>
          </div>
        </button>
      )}
    </div>
  );
};

export const Logo = ({ className, dark = false, size = 32, showText = true, testId = "logo" }) => (
  <span
    className={cn("group inline-flex items-center gap-2.5", className)}
    data-testid={testId}
    aria-label="RupeeTether"
  >
    <span
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ height: size, width: size }}
    >
      <span
        aria-hidden
        className="absolute inset-0 rounded-full opacity-70 blur-md transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.65) 0%, transparent 65%)" }}
      />
      <img
        src="/brand/logo.png"
        alt=""
        width={size}
        height={size}
        className="relative h-full w-full rounded-full object-contain drop-shadow-[0_6px_12px_hsl(44_86%_30%/0.35)] transition-transform duration-500 group-hover:rotate-[12deg]"
        loading="eager"
      />
    </span>
    {showText && (
      <span className={cn("font-display font-semibold tracking-tight", dark ? "text-white" : "text-foreground")}
        style={{ fontSize: Math.max(14, size * 0.52) }}>
        Rupee<span className="text-[hsl(var(--accent))]">↔</span>Tether
      </span>
    )}
  </span>
);

export const EmptyState = ({ icon: Icon = FileText, title, children, testId = "empty-state" }) => (
  <div data-testid={testId} className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-card px-6 py-10 text-center">
    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-secondary">
      <Icon className="h-5 w-5 text-muted-foreground" />
    </div>
    <p className="font-medium">{title}</p>
    {children && <div className="mt-1 max-w-sm text-sm text-muted-foreground">{children}</div>}
  </div>
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground sm:text-base">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
